import { create } from "zustand";
import { toast } from "react-toastify";
import {
  addIeComplaint,
  closeIeComplaint,
  getIeComplaintById,
  getIeComplaintClosedHistory,
  getIeComplaintKpiDetails,
  getIeComplaintList,
  getIeComplaintStatusOptions,
  getIeComplaintStatusFilterOptions,
  resolveIeComplaintImageUrl,
  updateIeComplaint,
} from "../../../../Apis/ieComplaintApi";

const mapActionHistoryItem = (item) => {
  const actionStatus = item.actionStatus;
  const isTerminal =
    actionStatus === 1 ||
    actionStatus === "1" ||
    actionStatus === 2 ||
    actionStatus === "2";

  return {
    sNo: item.sNo,
    actionTakenBy:
      (isTerminal ? item.closedBy : item.assignedBy) || item.actionTakenBy || "-",
    status: item.status || "-",
    actionRemarks: item.actionRemarks || "-",
    assignedOn: item.assignedOn || "",
    actionTakenOn: item.actionTakenOn || "",
    totalTime: item.totalTime || "",
  };
};

// Both "Closed" and "Invalid" are terminal/closure statuses (backend isClosed codes: 1 = Closed, 2 = Invalid).
const isTerminalComplaintItem = (item) =>
  item.isClosed === true ||
  item.isClosed === 1 ||
  item.isClosed === "1" ||
  item.isClosed === 2 ||
  item.isClosed === "2" ||
  item.status === "Closed" ||
  item.status === "Invalid";

const mapComplaintItem = (item) => ({
  complaintId: item.ieComplaintId ?? item.complaintId,
  complainantNo: item.complaintcode ?? item.complainantNo ?? "",
  contactNo: item.contactNo ?? "",
  kpiCategory: item.kpiType ?? item.kpiCategory ?? "",
  kpiTypeId: item.kpiTypeId ?? "",
  issueType: item.issueTypeName ?? item.issueType ?? "",
  issueTypeId: item.issueTypeId ?? item.ieissueTypeId ?? "",
  description: item.descriptionBefore ?? item.description ?? "",
  zoneId: item.zoneId?.toString() ?? "",
  zoneName: item.zoneName ?? "",
  wardId: item.wardId?.toString() ?? "",
  wardNo: item.wardName ?? item.wardNo ?? "",
  location: item.fAddress ?? item.location ?? "",
  complaintCreatedOn: item.createdDate ?? item.complaintDate ?? item.complaintCreatedOn ?? "",
  complaintClosureOn: isTerminalComplaintItem(item)
    ? item.modifiedDate ?? item.complaintClosureOn ?? ""
    : "",
  actualDuration: item.totalDuration ?? item.actualDuration ?? "",
  colonyName: item.colonyName ?? "",
  colonyId: item.colonyId?.toString() ?? "",
  latitude: item.fLat?.toString() ?? item.latitude?.toString() ?? "",
  longitude: item.fLng?.toString() ?? item.longitude?.toString() ?? "",
  status: item.status ?? (item.isClosed === true || item.isClosed === 1 || item.isClosed === "1" ? "Closed" : "Open"),
  isClosed: isTerminalComplaintItem(item),
  modeOfReporting: item.modeOfReporting ?? "",
  actionRemarks: item.actionRemarks ?? item.remarks ?? item.Remarks ?? "",
  beforeImage: item.fImgUrl ?? item.beforeImage ?? "",
  afterImage: item.cImgUrl ?? item.afterImage ?? item.completionImage ?? "",
  imageUrl: resolveIeComplaintImageUrl(item.fImgUrl ?? item.beforeImage ?? ""),
  closingImageUrl: resolveIeComplaintImageUrl(
    item.cImgUrl ?? item.afterImage ?? item.completionImage ?? ""
  ),
  closeTimeLatitude: item.cLat ?? item.closeTimeLatitude ?? "",
  closeTimeLongitude: item.cLng ?? item.closeTimeLongitude ?? "",
  closeTimeLocation: item.cAddress ?? item.closeTimeLocation ?? "",
  actionHistory: Array.isArray(item.actionHistory)
    ? item.actionHistory.map(mapActionHistoryItem)
    : [],
});

const mapComplaintList = (response) => {
  const list = response?.data ?? (Array.isArray(response) ? response : []);
  return Array.isArray(list) ? list.map(mapComplaintItem) : [];
};

const toPositiveNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/** Resolve grand total; ignore page-length `count` when a real total field exists. */
const resolveTotalCount = (response, dataLength) => {
  const firstItem = Array.isArray(response?.data) ? response.data[0] : null;

  const candidates = [
    response?.totalCount,
    response?.TotalCount,
    response?.totalRecords,
    response?.TotalRecords,
    response?.recordsTotal,
    response?.RecordsTotal,
    firstItem?.totalCount,
    firstItem?.TotalCount,
    firstItem?.totalRecords,
    firstItem?.TotalRecords,
  ];

  for (const value of candidates) {
    const n = toPositiveNumber(value);
    // Skip bogus 0 totals when the page already has rows.
    if (n == null || (n === 0 && dataLength > 0)) continue;
    return n;
  }

  // `count` is only trusted when it exceeds the current page length (grand total).
  const count = toPositiveNumber(response?.count ?? response?.Count);
  if (count != null && count > dataLength) return count;

  return dataLength;
};

const mapDetailItem = (response) => {
  const item = response?.data ?? response;
  if (!item) return null;
  return mapComplaintItem({
    ...item,
    beforeImage: item.fImgUrl ?? item.beforeImage,
    afterImage: item.cImgUrl ?? item.afterImage,
  });
};

const toList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const getErrorMessage = (error, fallback = "Unable to process IE complaint request.") => {
  const responseData = error?.response?.data;
  if (typeof responseData === "string" && responseData) return responseData;
  return responseData?.message || responseData?.Message || fallback;
};

export const DEFAULT_REPORT_ZONE_ID = "1";
export const DEFAULT_REPORT_ZONE_NAME = "Delhi Central Zone";

const getTodayDateString = () => new Date().toISOString().split("T")[0];

const emptyFilters = {
  zoneId: DEFAULT_REPORT_ZONE_ID,
  wardId: "",
  kpiCategory: "",
  fromDate: getTodayDateString(),
  toDate: getTodayDateString(),
  status: "",
};

export const useIeComplaintStore = create((set, get) => ({
  complaints: [],
  filteredComplaints: [],
  kpiCategoryOptions: [],
  issueTypeOptions: [],
  statusOptions: [],
  statusFilterOptions: [],
  filters: emptyFilters,
  isLoading: false,
  isSaving: false,
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
  },

  updateFilter: (name, value) =>
    set((state) => ({
      filters: {
        ...state.filters,
        [name]: value,
        ...(name === "zoneId" ? { wardId: "" } : {}),
      },
    })),

  fetchMasterOptions: async () => {
    try {
      const [kpiDetails, statusOptions, statusFilterOptions] = await Promise.all([
        getIeComplaintKpiDetails(),
        getIeComplaintStatusOptions(),
        getIeComplaintStatusFilterOptions(),
      ]);

      set({
        kpiCategoryOptions: kpiDetails.map((item) => ({
          kpiTypeId: item.kpitypeId ?? item.kpiTypeId,
          kpiType: item.kpitype ?? item.kpiType ?? "",
          kpiCode: item.kpicode ?? item.kpiCode ?? "",
        })),
        statusOptions: statusOptions
          .map((item) => item.name ?? item.status ?? "")
          .filter(Boolean),
        statusFilterOptions: statusFilterOptions
          .map((item) => item.name ?? item.status ?? "")
          .filter(Boolean),
      });

      return { success: true };
    } catch (error) {
      console.error("Error fetching IE complaint master options:", error);
      set({
        kpiCategoryOptions: [],
        statusOptions: [],
        statusFilterOptions: [],
      });
      return { success: false, error };
    }
  },

  fetchComplaints: async (options = {}) => {
    const { filters, pagination, kpiCategoryOptions } = get();
    const pageSize = options.pageSize ?? pagination.pageSize;
    const requestedPage = options.currentPage ?? pagination.currentPage;
    const nextFilters = {
      zoneId: options.zoneId ?? filters.zoneId,
      wardId: options.wardId ?? filters.wardId,
      kpiCategory: options.kpiCategory ?? filters.kpiCategory,
      fromDate: options.fromDate ?? filters.fromDate,
      toDate: options.toDate ?? filters.toDate,
      status: options.status ?? filters.status,
    };

    const selectedKpi = kpiCategoryOptions.find(
      (item) =>
        String(item.kpiTypeId) === String(nextFilters.kpiCategory) ||
        item.kpiType === nextFilters.kpiCategory
    );

    set({ isLoading: true, filters: nextFilters });

    try {
      // Load full filtered list so page numbers reflect the real record total.
      // (GetAll does not reliably return a grand-total field with small pageSize.)
      const response = await getIeComplaintList({
        zoneId: nextFilters.zoneId,
        wardId: nextFilters.wardId,
        kpiTypeId: selectedKpi?.kpiTypeId ?? nextFilters.kpiCategory,
        status: nextFilters.status,
        fromDate: nextFilters.fromDate,
        toDate: nextFilters.toDate,
        pageNumber: 1,
        pageSize: 100000,
      });

      const data = mapComplaintList(response);
      const totalCount = resolveTotalCount(response, data.length);
      const totalPages = Math.max(1, Math.ceil(totalCount / pageSize) || 1);
      const currentPage = Math.min(Math.max(1, requestedPage), totalPages);

      set({
        complaints: data,
        filteredComplaints: data,
        pagination: {
          currentPage,
          pageSize,
          totalCount,
        },
        isLoading: false,
      });

      return { success: true, data };
    } catch (error) {
      console.error("Error fetching IE complaints:", error);
      set({
        complaints: [],
        filteredComplaints: [],
        pagination: { ...get().pagination, totalCount: 0 },
        isLoading: false,
      });
      return { success: false, error };
    }
  },

  changePage: (page) => {
    const { pagination } = get();
    const totalPages = Math.max(
      1,
      Math.ceil((pagination.totalCount || 0) / pagination.pageSize) || 1
    );
    const currentPage = Math.min(Math.max(1, page), totalPages);
    set({ pagination: { ...pagination, currentPage } });
  },

  changePageSize: (pageSize) => {
    const { pagination } = get();
    const nextSize = Math.max(1, Number(pageSize) || pagination.pageSize);
    set({
      pagination: {
        ...pagination,
        pageSize: nextSize,
        currentPage: 1,
      },
    });
  },

  applyFilters: async () => {
    const { filters } = get();
    return get().fetchComplaints({ ...filters, currentPage: 1 });
  },

  resetFilters: async (defaultZoneId = DEFAULT_REPORT_ZONE_ID) => {
    const nextFilters = {
      ...emptyFilters,
      zoneId: defaultZoneId || DEFAULT_REPORT_ZONE_ID,
    };
    set({ filters: nextFilters });
    return get().fetchComplaints({ ...nextFilters, currentPage: 1 });
  },

  fetchComplaintById: async (complaintId) => {
    try {
      const response = await getIeComplaintById(complaintId);
      const data = mapDetailItem(response);
      return { success: Boolean(data), data };
    } catch (error) {
      console.error("Error fetching IE complaint details:", error);
      toast.error(getErrorMessage(error, "Unable to fetch complaint details."));
      return { success: false, error };
    }
  },

  fetchActionHistory: async (complaintId, complaintCode) => {
    try {
      const resolvedCode =
        complaintCode ||
        [...get().filteredComplaints, ...get().complaints].find(
          (item) => String(item.complaintId) === String(complaintId)
        )?.complainantNo;

      if (!resolvedCode) {
        return { success: false, data: [] };
      }

      const response = await getIeComplaintClosedHistory(resolvedCode);
      const history = toList(response).map(mapActionHistoryItem);

      const matchId = String(complaintId);
      set((state) => ({
        filteredComplaints: state.filteredComplaints.map((item) =>
          String(item.complaintId) === matchId ? { ...item, actionHistory: history } : item
        ),
        complaints: state.complaints.map((item) =>
          String(item.complaintId) === matchId ? { ...item, actionHistory: history } : item
        ),
      }));

      return { success: true, data: history };
    } catch (error) {
      console.error("Error fetching IE complaint action history:", error);
      return { success: false, error };
    }
  },

  saveComplaint: async (payload, isEdit = false) => {
    set({ isSaving: true });

    try {
      const { kpiCategoryOptions, issueTypeOptions } = get();

      const kpiTypeId =
        payload.kpiTypeId ??
        kpiCategoryOptions.find(
          (item) =>
            String(item.kpiTypeId) === String(payload.kpiCategory) ||
            item.kpiType === payload.kpiCategory
        )?.kpiTypeId;

      const issueTypeId =
        payload.issueTypeId ??
        issueTypeOptions.find(
          (item) =>
            String(item.issueTypeId) === String(payload.issueType) ||
            item.issueType === payload.issueType
        )?.issueTypeId;

      const apiPayload = {
        ...payload,
        kpiTypeId,
        issueTypeId,
      };

      const response = isEdit
        ? await updateIeComplaint(apiPayload)
        : await addIeComplaint(apiPayload);

      if (response?.success === false) {
        toast.error(response?.message || "Unable to save IE complaint details.");
        return { success: false };
      }

      const targetIsClosed = payload.status === "Closed";
      const wasClosed = Boolean(payload.wasClosed);
      const isUpdateStatusOption =
        payload.status === "Closed" || payload.status === "Invalid";
      const statusChanged = isEdit && targetIsClosed !== wasClosed;
      const closingImageUploaded =
        isEdit && targetIsClosed && apiPayload.imageFile instanceof File;
      const shouldCloseComplaint =
        isEdit &&
        isUpdateStatusOption &&
        (statusChanged ||
          closingImageUploaded ||
          payload.status === "Invalid");

      if (shouldCloseComplaint) {
        const closeResponse = await closeIeComplaint(apiPayload);
        if (closeResponse?.success === false) {
          toast.error(closeResponse?.message || "Unable to update complaint status.");
          return { success: false };
        }

        if (
          closeResponse?.isClosed === 1 ||
          closeResponse?.isClosed === true ||
          closeResponse?.isClosed === "1"
        ) {
          apiPayload.isClosed = true;
          apiPayload.status = "Closed";
        } else if (closeResponse?.isClosed === 2 || closeResponse?.isClosed === "2") {
          apiPayload.isClosed = true;
          apiPayload.status = "Invalid";
        }
        if (closeResponse?.completionImage) {
          apiPayload.completionImage = closeResponse.completionImage;
          apiPayload.closingImageUrl = resolveIeComplaintImageUrl(
            closeResponse.completionImage
          );
        }
        if (closeResponse?.complaintCode) {
          apiPayload.complainantNo = closeResponse.complaintCode;
        }
      }

      toast.success(
        response?.message ||
          (isEdit ? "IE Complaint updated successfully." : "IE Complaint added successfully.")
      );

      await get().fetchComplaints();

      return { success: true, data: response };
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to save IE complaint details."));
      return { success: false, error };
    } finally {
      set({ isSaving: false });
    }
  },
  fetchAllComplaintsForExport: async () => {
    const { filters, kpiCategoryOptions } = get();

    const selectedKpi = kpiCategoryOptions.find(
      (item) =>
        String(item.kpiTypeId) === String(filters.kpiCategory) ||
        item.kpiType === filters.kpiCategory
    );

    try {
      const response = await getIeComplaintList({
        zoneId: filters.zoneId,
        wardId: filters.wardId,
        kpiTypeId: selectedKpi?.kpiTypeId ?? filters.kpiCategory,
        status: filters.status,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        pageNumber: 1,
        pageSize: 100000, // anni records fetch avvadaniki large number
      });

      return mapComplaintList(response);
    } catch (error) {
      console.error("Error fetching all IE complaints for export:", error);
      return [];
    }
  },
}));
